/* Usage and cost for the current billing period, and the limits that govern it.
 *
 * Three things this screen is deliberate about.
 *
 * It says the numbers **lag**. Usage is read from what has been drained to durable storage, so the
 * most recent minute of counting is not here. Rendering it as a live figure would make a customer's
 * arithmetic disagree with ours for a reason neither side could see.
 *
 * It shows **unpriced meters as a warning**, not a footnote. A meter used with no rate is a billing
 * configuration gap; the API refuses to charge it zero, and a screen that hid it would put the
 * failure back.
 *
 * It offers **no controls**. Setting a limit or a price needs `configure`, which only `admin`
 * holds, and a disabled button explains less than its absence.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CostSummary,
  Meter,
  Quota,
  UsageLine,
} from '@/shared/api/generated/metering';
import { Alert } from '@/shared/ui/Alert';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useCost, useQuotas, useUsage } from '../api/metering';
import {
  hasUnpricedUsage,
  meterLabelKey,
  quotaTone,
  usageByMeter,
} from '../model/metering';

import type { QuotaTone } from '../model/metering';

const TONE_COLOURS: Record<QuotaTone, string> = {
  ok: 'var(--status-ok)',
  warn: 'var(--status-warn)',
  breach: 'var(--status-error)',
};

const UNITS = new Intl.NumberFormat();

function MeterName({ meter }: { readonly meter: Meter }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <strong>{t(meterLabelKey(meter))}</strong>
      <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
        {meter}
      </div>
    </>
  );
}

function UsageRow({
  line,
  cost,
  currency,
}: {
  readonly line: UsageLine;
  readonly cost: string | null;
  readonly currency: string | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <tr>
      <td>
        <MeterName meter={line.meter} />
      </td>
      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {UNITS.format(line.units)}
      </td>
      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {/* Never a zero, and never a dash: an unpriced meter says so in words, because a blank
            cell in a money column reads as "nothing owed". */}
        {cost === null ? (
          <em style={{ color: 'var(--status-warn)' }}>{t('metering.unpriced')}</em>
        ) : (
          `${cost}${currency === null ? '' : ` ${currency}`}`
        )}
      </td>
    </tr>
  );
}

function QuotaRow({
  quota,
  consumed,
}: {
  readonly quota: Quota;
  readonly consumed: number;
}): ReactElement {
  const { t } = useTranslation();
  const tone = quotaTone(quota, consumed);
  return (
    <tr>
      <td>
        <MeterName meter={quota.meter} />
      </td>
      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ color: TONE_COLOURS[tone] }}>{UNITS.format(consumed)}</span>
        {' / '}
        {UNITS.format(quota.unit_limit)}
      </td>
      <td>{t(`metering.exhaustion.${quota.on_exhaustion}`)}</td>
      <td style={{ color: TONE_COLOURS[tone] }}>{t(`metering.quotaTone.${tone}`)}</td>
    </tr>
  );
}

function costFor(summary: CostSummary | undefined, meter: Meter): string | null {
  if (summary === undefined) return null;
  return summary.lines.find((line) => line.meter === meter)?.cost ?? null;
}

export function UsageAndCost(): ReactElement {
  const { t } = useTranslation();
  const usage = useUsage();
  const cost = useCost();
  const quotas = useQuotas();

  const header = (
    <PageHeader title={t('metering.title')} lede={t('metering.description')} />
  );

  if (usage.isLoading) {
    return (
      <>
        {header}
        <SkeletonRows rows={4} />
      </>
    );
  }

  if (usage.isError) {
    return (
      <>
        {header}
        <Alert tone="error" title={t('metering.loadFailed')}>
          {usage.error.message}
        </Alert>
      </>
    );
  }

  const lines = usage.data ?? [];
  const summary = cost.data;
  const currency = summary?.currency ?? null;
  const configured = quotas.data ?? [];
  const consumedByMeter = usageByMeter(lines);

  return (
    <>
      {header}
      <Alert tone="notice" title={t('metering.lag.title')}>
        {t('metering.lag.body')}
      </Alert>
      {summary !== undefined && hasUnpricedUsage(summary) ? (
        <Alert tone="warning" title={t('metering.unpricedAlert.title')}>
          {t('metering.unpricedAlert.body', {
            meters: summary.unpriced_meters
              .map((meter) => t(meterLabelKey(meter)))
              .join(', '),
          })}
        </Alert>
      ) : null}

      {lines.length === 0 ? (
        <EmptyState
          title={t('metering.empty.title')}
          guidance={t('metering.empty.body')}
        />
      ) : (
        <DataTable
          caption={t('metering.usage.caption')}
          columns={[
            { key: 'meter', label: t('metering.column.meter') },
            { key: 'units', label: t('metering.column.units') },
            { key: 'cost', label: t('metering.column.cost') },
          ]}
        >
          {lines.map((line) => (
            <UsageRow
              key={line.meter}
              line={line}
              cost={costFor(summary, line.meter)}
              currency={currency}
            />
          ))}
        </DataTable>
      )}

      {summary !== undefined && summary.lines.length > 0 ? (
        <p style={{ textAlign: 'right', fontWeight: 600 }}>
          {t('metering.total', { total: summary.total, currency: currency ?? '' })}
        </p>
      ) : null}

      {configured.length === 0 ? (
        <Alert tone="notice" title={t('metering.noQuota.title')}>
          {t('metering.noQuota.body')}
        </Alert>
      ) : (
        <DataTable
          caption={t('metering.quotas.caption')}
          columns={[
            { key: 'meter', label: t('metering.column.meter') },
            { key: 'consumed', label: t('metering.column.consumed') },
            { key: 'exhaustion', label: t('metering.column.exhaustion') },
            { key: 'standing', label: t('metering.column.standing') },
          ]}
        >
          {configured.map((quota) => (
            <QuotaRow
              key={quota.meter}
              quota={quota}
              consumed={consumedByMeter.get(quota.meter) ?? 0}
            />
          ))}
        </DataTable>
      )}
    </>
  );
}
